import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { BookOpen, Plus, Play, FileText, Video, Music, Trash2, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';

export default function Learning() {
  const { isAdmin } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [newContent, setNewContent] = useState({
    title: '',
    description: '',
    type: 'video',
    url: '',
    category: 'khassaide'
  });
  const [contentToDelete, setContentToDelete] = useState(null);
  const [deletingContent, setDeletingContent] = useState(false);

  const contentTypes = [
    { value: 'video', label: 'Vidéo', icon: Video },
    { value: 'audio', label: 'Audio', icon: Music },
    { value: 'document', label: 'Document', icon: FileText }
  ];

  const categories = [
    { value: 'khassaide', label: 'Khassaïdes' },
    { value: 'conference', label: 'Conférences' },
    { value: 'cours', label: 'Cours' },
    { value: 'priere', label: 'Prières' },
    { value: 'histoire', label: 'Histoire' },
    { value: 'autre', label: 'Autre' }
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const contentQuery = query(collection(db, 'learning_content'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(contentQuery);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setContent(data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContent = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await addDoc(collection(db, 'learning_content'), {
        ...newContent,
        createdAt: serverTimestamp(),
        views: 0
      });

      toast.success('Contenu ajouté avec succès');
      setDialogOpen(false);
      setNewContent({ title: '', description: '', type: 'video', url: '', category: 'khassaide' });
      fetchContent();
    } catch (error) {
      console.error('Error creating content:', error);
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteContent = async () => {
    if (!contentToDelete) return;
    setDeletingContent(true);
    try {
      await deleteDoc(doc(db, 'learning_content', contentToDelete.id));
      toast.success('Contenu supprimé avec succès');
      fetchContent();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingContent(false);
      setContentToDelete(null);
    }
  };

  const filteredContent = activeTab === 'all'
    ? content
    : content.filter(c => c.type === activeTab);

  const getTypeIcon = (type) => {
    const icons = { video: Video, audio: Music, document: FileText };
    const Icon = icons[type] || FileText;
    return <Icon className="h-5 w-5" />;
  };

  const getTypeColor = (type) => {
    const colors = {
      video: 'bg-red-100 text-red-800',
      audio: 'bg-purple-100 text-purple-800',
      document: 'bg-blue-100 text-blue-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getThumbnail = (url, type) => {
    if (type !== 'video') return null;

    // YouTube
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch && youtubeMatch[1]) {
      return `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`;
    }

    return null;
  };

  const getEmbedUrl = (url) => {
    // YouTube
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch && youtubeMatch[1]) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1`;
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="learning-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Espace Culte</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Ressources spirituelles et apprentissage</p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto" data-testid="add-content-btn">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter du contenu
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Ajouter un contenu</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateContent} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre</Label>
                  <Input
                    id="title"
                    value={newContent.title}
                    onChange={(e) => setNewContent({ ...newContent, title: e.target.value })}
                    placeholder="Titre du contenu"
                    required
                    data-testid="content-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={newContent.type} onValueChange={(value) => setNewContent({ ...newContent, type: value })}>
                      <SelectTrigger data-testid="content-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {contentTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select value={newContent.category} onValueChange={(value) => setNewContent({ ...newContent, category: value })}>
                      <SelectTrigger data-testid="content-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="url">URL du contenu</Label>
                  <Input
                    id="url"
                    value={newContent.url}
                    onChange={(e) => setNewContent({ ...newContent, url: e.target.value })}
                    placeholder="Lien YouTube, Drive, etc."
                    required
                    data-testid="content-url-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optionnel)</Label>
                  <Textarea
                    id="description"
                    value={newContent.description}
                    onChange={(e) => setNewContent({ ...newContent, description: e.target.value })}
                    placeholder="Description du contenu"
                    rows={3}
                    data-testid="content-description-input"
                  />
                </div>

                <Button type="submit" className="w-full" disabled={creating} data-testid="submit-content-btn">
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Ajout...
                    </>
                  ) : (
                    'Ajouter'
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">Tout</TabsTrigger>
          <TabsTrigger value="video" data-testid="tab-video">Vidéos</TabsTrigger>
          <TabsTrigger value="audio" data-testid="tab-audio">Audio</TabsTrigger>
          <TabsTrigger value="document" data-testid="tab-document">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredContent.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredContent.map((item) => {
                const thumbnail = getThumbnail(item.url, item.type);
                const embedUrl = getEmbedUrl(item.url);
                return (
                  <Card key={item.id} className="card-hover border-border/50 overflow-hidden flex flex-col" data-testid={`content-card-${item.id}`}>
                    {item.type === 'video' && embedUrl ? (
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="aspect-video bg-muted flex items-center justify-center relative group overflow-hidden w-full">
                            {thumbnail ? (
                              <img
                                src={thumbnail}
                                alt={item.title}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                            ) : (
                              <div className={`w-16 h-16 rounded-full ${getTypeColor(item.type)} flex items-center justify-center`}>
                                {getTypeIcon(item.type)}
                              </div>
                            )}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                              <div className="w-14 h-14 rounded-full bg-white/90 shadow-lg flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform">
                                <Play className="h-6 w-6 text-primary ml-1" />
                              </div>
                            </div>
                          </button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-4xl p-0 overflow-hidden bg-black border-none">
                          <div className="aspect-video w-full">
                            <iframe
                              src={embedUrl}
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title={item.title}
                            />
                          </div>
                        </DialogContent>
                      </Dialog>
                    ) : (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aspect-video bg-muted flex items-center justify-center relative group overflow-hidden"
                      >
                        {thumbnail ? (
                          <img
                            src={thumbnail}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className={`w-16 h-16 rounded-full ${getTypeColor(item.type)} flex items-center justify-center`}>
                            {getTypeIcon(item.type)}
                          </div>
                        )}

                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-14 h-14 rounded-full bg-white/90 shadow-lg flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform">
                            {item.type === 'video' ? (
                              <Play className="h-6 w-6 text-primary ml-1" />
                            ) : item.type === 'document' ? (
                              <FileText className="h-6 w-6 text-primary" />
                            ) : (
                              <Music className="h-6 w-6 text-primary" />
                            )}
                          </div>
                        </div>
                      </a>
                    )}

                    <CardContent className="p-4 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <Badge className={getTypeColor(item.type)}>
                          {categories.find(c => c.value === item.category)?.label || item.category}
                        </Badge>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
                            onClick={(e) => {
                              e.preventDefault();
                              setContentToDelete(item);
                            }}
                            data-testid={`delete-content-${item.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <h3 className="font-semibold text-foreground mb-1 line-clamp-2">{item.title}</h3>
                      {item.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3 flex-1">{item.description}</p>
                      )}
                      <div className="mt-auto pt-3 border-t border-border/50 flex justify-between items-center">
                        {item.type === 'video' && embedUrl ? (
                          <Dialog>
                            <DialogTrigger asChild>
                              <button className="text-sm font-medium text-primary hover:underline flex items-center group">
                                Regarder
                                <Play className="h-3 w-3 ml-1 transition-transform group-hover:scale-110" />
                              </button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-4xl p-0 overflow-hidden bg-black border-none">
                              <div className="aspect-video w-full">
                                <iframe
                                  src={embedUrl}
                                  className="w-full h-full"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  title={item.title}
                                />
                              </div>
                            </DialogContent>
                          </Dialog>
                        ) : (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm font-medium text-primary hover:underline group"
                          >
                            Ouvrir
                            <ExternalLink className="h-3 w-3 ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </a>
                        )}
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                          {item.type}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="border-border/50">
              <CardContent className="py-12 text-center">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium text-foreground mb-2">Aucun contenu</h3>
                <p className="text-muted-foreground">
                  {isAdmin ? 'Ajoutez votre premier contenu' : 'Aucun contenu disponible pour le moment'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Content Confirmation Modal */}
      <ConfirmDeleteDialog
        open={!!contentToDelete}
        onOpenChange={(open) => !open && setContentToDelete(null)}
        onConfirm={handleDeleteContent}
        title="Supprimer ce contenu ?"
        description="Cette action est irréversible. Le contenu sera définitivement supprimé de l'espace culte."
        itemName={contentToDelete?.title}
        confirmText="Supprimer le contenu"
        loading={deletingContent}
        variant="danger"
      />
    </div>
  );
}
